import { useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, Field, IconButton, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { action, records } from '../../lib/api.ts';
import { usePlaces } from '../../lib/places.tsx';
import type { Place } from '../../lib/types.ts';
import { useUi } from '../../lib/ui-state.tsx';
import { ManageLayout, useAction, useRecords, useSave } from './common.tsx';

interface Station {
  id: string;
  name: string;
  kind: 'kitchen' | 'bar';
  sequence: number;
}
interface Register {
  id: string;
  name: string;
  active: boolean;
  location: string | null;
}

/** Kitchen and bar stations (where tickets go) and the registers (where money is taken). */
export function Setup() {
  const { fail, toast } = useUi();
  const { rows: stations, reload: reloadStations } = useRecords<Station>('restaurant.station', { order_by: 'sequence' });
  const { rows: registers, reload: reloadRegisters } = useRecords<Register>('pos.register', { order_by: 'name' });
  const { data: open, reload: reloadOpen } = useAction<{ id: string; session: unknown }[]>('pos.registers');
  const [station, setStation] = useState<Station | 'new' | null>(null);
  const [register, setRegister] = useState<Register | 'new' | null>(null);
  const hasShift = (id: string) => !!open?.find((r) => r.id === id)?.session;
  const { places, multi, name: placeName, here } = usePlaces();
  const placeOf = (r: Register) => r.location ?? places.find((p) => p.main)?.id ?? null;

  const toggle = async (r: Register) => {
    try {
      await records.update('pos.register', r.id, { active: !r.active });
      toast(r.active ? t('setup.register_off', { name: r.name }) : t('setup.register_on', { name: r.name }));
      void reloadRegisters();
      void reloadOpen();
    } catch (err) {
      fail(err);
    }
  };

  return (
    <ManageLayout title={t('manage.setup')} subtitle={t('setup.subtitle')}>
      <div className="setup-grid">
        <section className="list-card" aria-labelledby="stations-title">
          <div className="list-card-head">
            <div>
              <h2 id="stations-title">{t('setup.stations')}</h2>
              <p className="muted">{t('setup.stations_hint')}</p>
            </div>
            <Button icon="plus" onClick={() => setStation('new')}>
              {t('setup.add_station')}
            </Button>
          </div>
          {!stations ? (
            <Skeleton h={120} />
          ) : (
            stations.map((s) => (
              <div key={s.id} className="list-row">
                <span className="list-icon" aria-hidden="true">
                  <Icon name={s.kind === 'bar' ? 'bag' : 'chef'} size={20} />
                </span>
                <span className="list-main">
                  <span className="list-title">{s.name}</span>
                  <span className="list-sub">{t(`setup.kind_${s.kind}`)}</span>
                </span>
                <IconButton icon="edit" label={t('setup.edit_name', { name: s.name })} onClick={() => setStation(s)} />
              </div>
            ))
          )}
        </section>

        <section className="list-card" aria-labelledby="registers-title">
          <div className="list-card-head">
            <div>
              <h2 id="registers-title">{t('setup.registers')}</h2>
              <p className="muted">{t('setup.registers_hint')}</p>
            </div>
            <Button icon="plus" onClick={() => setRegister('new')}>
              {t('setup.add_register')}
            </Button>
          </div>
          {!registers ? (
            <Skeleton h={120} />
          ) : (
            registers.map((r) => {
              const shift = hasShift(r.id);
              return (
                <div key={r.id} className={`list-row${r.active ? '' : ' archived'}`}>
                  <span className="list-icon" aria-hidden="true">
                    <Icon name="register" size={20} />
                  </span>
                  <span className="list-main">
                    <span className="list-title">{r.name}</span>
                    <span className="list-sub">
                      {multi ? (
                        <span className="place-tag">
                          <Icon name="pin" size={13} />
                          {placeName(placeOf(r))}
                        </span>
                      ) : null}
                      {shift ? (
                        <StatusBadge tone="ok" icon="dot" label={t('setup.shift_open')} />
                      ) : r.active ? (
                        <StatusBadge tone="neutral" icon="circle" label={t('setup.in_use')} />
                      ) : (
                        <StatusBadge tone="neutral" icon="pause" label={t('setup.not_in_use')} />
                      )}
                    </span>
                  </span>
                  <Switch
                    checked={r.active}
                    disabled={shift}
                    label={shift ? t('setup.close_shift_first', { name: r.name }) : t('setup.use_register', { name: r.name })}
                    onChange={() => void toggle(r)}
                  />
                  <IconButton icon="edit" label={t('setup.edit_name', { name: r.name })} onClick={() => setRegister(r)} />
                </div>
              );
            })
          )}
          {open && registers && open.length === 0 ? <p className="form-hint">{t('setup.no_register')}</p> : null}
        </section>
      </div>

      {station ? (
        <NameDialog
          title={station === 'new' ? t('setup.add_station') : station.name}
          initial={station === 'new' ? '' : station.name}
          taken={(stations ?? []).filter((s) => station === 'new' || s.id !== station.id).map((s) => s.name)}
          kind={station === 'new' ? 'kitchen' : station.kind}
          onClose={() => setStation(null)}
          onSave={async (name, kind) => {
            if (station === 'new') {
              const sequence = Math.max(0, ...(stations ?? []).map((s) => s.sequence)) + 10;
              await records.create('restaurant.station', { name, kind, sequence });
            } else await records.update('restaurant.station', station.id, { name, kind });
            setStation(null);
            void reloadStations();
          }}
        />
      ) : null}
      {register ? (
        <NameDialog
          title={register === 'new' ? t('setup.add_register') : register.name}
          initial={register === 'new' ? '' : register.name}
          taken={(registers ?? []).filter((r) => register === 'new' || r.id !== register.id).map((r) => r.name)}
          places={multi ? places : undefined}
          place={register === 'new' ? (here?.id ?? null) : placeOf(register)}
          onClose={() => setRegister(null)}
          onSave={async (name, _kind, location) => {
            await action('pos.save_register', { ...(register === 'new' ? {} : { id: register.id }), name, location });
            setRegister(null);
            void reloadRegisters();
            void reloadOpen();
          }}
        />
      ) : null}
    </ManageLayout>
  );
}

function NameDialog({
  title,
  initial,
  taken,
  kind: initialKind,
  places,
  place: initialPlace,
  onClose,
  onSave,
}: {
  title: string;
  initial: string;
  taken: string[];
  kind?: Station['kind'];
  /** Where a register stands, when the business has several locations. */
  places?: Place[];
  place?: string | null;
  onClose: () => void;
  onSave: (name: string, kind: Station['kind'], place: string | null) => Promise<void>;
}) {
  const { busy, run } = useSave();
  const [name, setName] = useState(initial);
  const [place, setPlace] = useState<string | null>(initialPlace ?? null);
  const [kind, setKind] = useState<Station['kind']>(initialKind ?? 'kitchen');
  const clean = name.trim();
  const isTaken = taken.some((x) => x.toLowerCase() === clean.toLowerCase());
  const save = () => {
    if (!clean || isTaken) return;
    void run(() => onSave(clean, kind, place), initial ? t('menu.saved', { name: clean }) : t('menu.added', { name: clean }));
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!clean || isTaken}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <Field label={t('menu.name')} error={isTaken ? t('setup.name_taken') : null}>
          <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        {places ? (
          <Field label={t('places.location')} hint={t('setup.register_place_hint')}>
            <select className="input" value={place ?? ''} onChange={(e) => setPlace(e.target.value)}>
              {places.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {initialKind ? (
          <fieldset className="option-group">
            <legend>{t('setup.kind')}</legend>
            <div className="segmented" role="radiogroup">
              {(['kitchen', 'bar'] as const).map((k) => (
                <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
                  {t(`setup.kind_${k}`)}
                </button>
              ))}
            </div>
          </fieldset>
        ) : null}
      </form>
    </Dialog>
  );
}
